import { DEFAULT_PERIOD_DAYS } from "@app/components/agent_builder/observability/constants";
import { useSendNotification } from "@app/hooks/useNotification";
import type {
  AgentMessageFeedbackType,
  AgentMessageFeedbackWithMetadataType,
} from "@app/lib/api/assistant/feedback";
import type { GetContextOriginResponse } from "@app/lib/api/assistant/observability/context_origin";
import type { GetDatasourceRetrievalResponse } from "@app/lib/api/assistant/observability/datasource_retrieval";
import type { GetDatasourceRetrievalDocumentsResponse } from "@app/lib/api/assistant/observability/datasource_retrieval_documents";
import type { GetFeedbackDistributionResponse } from "@app/lib/api/assistant/observability/feedback_distribution";
import type {
  GetErrorRateResponse,
  GetLatencyResponse,
  GetUsageMetricsResponse,
} from "@app/lib/api/assistant/observability/messages_metrics";
import type { GetSkillExecutionResponse } from "@app/lib/api/assistant/observability/skill_execution";
import type { GetToolExecutionResponse } from "@app/lib/api/assistant/observability/tool_execution";
import type {
  GetToolLatencyResponse,
  ToolLatencyRow,
  ToolLatencyView,
} from "@app/lib/api/assistant/observability/tool_latency";
import type { GetToolStepIndexResponse } from "@app/lib/api/assistant/observability/tool_step_index";
import type { GetVersionMarkersResponse } from "@app/lib/api/assistant/observability/version_markers";
import { clientFetch } from "@app/lib/egress/client";
import type {
  FetchAgentTemplateResponse,
  FetchAssistantTemplatesResponse,
} from "@app/lib/resources/template_resource";
import {
  emptyArray,
  getErrorFromResponse,
  useFetcher,
  useSWRInfiniteWithDefaults,
  useSWRWithDefaults,
} from "@app/lib/swr/swr";
import { BROWSER_TIMEZONE } from "@app/lib/swr/workspaces";
import type { GetAgentUsageResponseBody } from "@app/types/api/assistant/agent_usage";
import type { GetSlackChannelsLinkedWithAgentResponseBody } from "@app/types/api/assistant/builder/slack/channels_linked_with_agent";
import type { GetAgentConfigurationsResponseBody } from "@app/types/api/assistant/configuration";
import type { GetAgentMcpConfigurationsResponseBody } from "@app/types/api/assistant/mcp_configurations";
import type { GetAgentOverviewResponseBody } from "@app/types/api/assistant/observability/overview";
import type { GetAgentSummaryResponseBody } from "@app/types/api/assistant/observability/summary";
import type { PostAgentUserFavoriteRequestBody } from "@app/types/api/assistant/user_relation";
import type { GetMemberResponseBody } from "@app/types/api/user";
import type {
  AgentConfigurationType,
  AgentsGetViewType,
  LightAgentConfigurationType,
} from "@app/types/assistant/agent";
import { normalizeError } from "@app/types/shared/utils/error_utils";
import type { LightWorkspaceType, UserType } from "@app/types/user";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Fetcher } from "swr";
import { useSWRConfig } from "swr";

export function useAgentMcpConfigurations({
  workspaceId,
  agentConfigurationId,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const mcpConfigurationsFetcher: Fetcher<GetAgentMcpConfigurationsResponseBody> =
    fetcher;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled
      ? null
      : `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/mcp_configurations`,
    mcpConfigurationsFetcher,
    { disabled }
  );

  return {
    configurations: data?.configurations ?? emptyArray(),
    isAgentMcpConfigurationsLoading: !error && !data && !disabled,
    isAgentMcpConfigurationsError: error,
    isAgentMcpConfigurationsValidating: isValidating,
  };
}

export function useAssistantTemplates() {
  const { fetcher } = useFetcher();
  const assistantTemplatesFetcher: Fetcher<FetchAssistantTemplatesResponse> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/templates`,
    assistantTemplatesFetcher
  );

  return {
    assistantTemplates: data?.templates ?? emptyArray(),
    isAssistantTemplatesLoading: !error && !data,
    isAssistantTemplatesError: error,
    mutateAssistantTemplates: mutate,
  };
}

export function useAssistantTemplate({
  templateId,
}: {
  templateId: string | null;
}) {
  const { fetcher } = useFetcher();
  const assistantTemplateFetcher: Fetcher<FetchAgentTemplateResponse> = fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    templateId !== null ? `/api/templates/${templateId}` : null,
    assistantTemplateFetcher
  );

  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    assistantTemplate: data ? data : null,
    isAssistantTemplateLoading: !error && !data,
    isAssistantTemplateError: error,
    mutateAssistantTemplate: mutate,
  };
}

/*
 * Agent configurations. A null agentsGetView means no fetching
 */
export function useAgentConfigurations({
  workspaceId,
  agentsGetView,
  includes = [],
  limit,
  sort,
  disabled,
  revalidate,
}: {
  workspaceId: string;
  agentsGetView: AgentsGetViewType | null;
  includes?: ("authors" | "usage" | "feedbacks" | "editors")[];
  limit?: number;
  sort?: "alphabetical" | "priority";
  disabled?: boolean;
  revalidate?: boolean;
}) {
  const { fetcher } = useFetcher();
  const agentConfigurationsFetcher: Fetcher<GetAgentConfigurationsResponseBody> =
    fetcher;

  // Function to generate query parameters.
  function getQueryString() {
    const params = new URLSearchParams();
    if (typeof agentsGetView === "string") {
      params.append("view", agentsGetView);
    }
    if (includes.includes("usage")) {
      params.append("withUsage", "true");
    }
    if (includes.includes("authors")) {
      params.append("withAuthors", "true");
    }
    if (includes.includes("editors")) {
      params.append("withEditors", "true");
    }
    if (includes.includes("feedbacks")) {
      params.append("withFeedbacks", "true");
    }

    if (limit) {
      params.append("limit", limit.toString());
    }

    if (sort) {
      params.append("sort", sort);
    }

    return params.toString();
  }

  const queryString = getQueryString();

  const key = `/api/w/${workspaceId}/assistant/agent_configurations?${queryString}`;
  const { cache } = useSWRConfig();
  const inCache = typeof cache.get(key) !== "undefined";

  const { data, error, mutate, mutateRegardlessOfQueryParams, isValidating } =
    useSWRWithDefaults(agentsGetView ? key : null, agentConfigurationsFetcher, {
      disabled,
      revalidateOnMount: !inCache || revalidate,
      revalidateOnFocus: !inCache || revalidate,
    });

  return {
    agentConfigurations: data
      ? data.agentConfigurations
      : emptyArray<LightAgentConfigurationType>(),
    isAgentConfigurationsLoading: !error && !data && !disabled,
    isAgentConfigurationsError: error,
    mutate,
    mutateRegardlessOfQueryParams,
    isAgentConfigurationsValidating: isValidating,
  };
}

// This is the call that is required for the new conversation page to load all views on that page.
// All elements that are involved in that page should rely on it to avoid concurrent calls to
// getAgentConfigurations at the initial page load.
export function useUnifiedAgentConfigurations({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const {
    agentConfigurations: agentConfigurationsWithAuthors,
    isAgentConfigurationsLoading: isAgentConfigurationsWithAuthorsLoading,
    isAgentConfigurationsValidating,
    mutate,
    mutateRegardlessOfQueryParams,
  } = useAgentConfigurations({
    workspaceId,
    agentsGetView: "list",
    includes: ["authors", "usage"],
    disabled,
  });

  return {
    agentConfigurations: agentConfigurationsWithAuthors,
    isLoading:
      isAgentConfigurationsWithAuthorsLoading ||
      isAgentConfigurationsValidating,
    mutate,
    mutateRegardlessOfQueryParams,
  };
}

export function useAccessibleAgentIds({
  workspaceId,
}: {
  workspaceId: string;
}): Set<string> {
  const { agentConfigurations } = useUnifiedAgentConfigurations({
    workspaceId,
  });
  return useMemo(
    () => new Set(agentConfigurations.map((a) => a.sId)),
    [agentConfigurations]
  );
}

export function useAgentConfiguration({
  workspaceId,
  agentConfigurationId,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const agentConfigurationFetcher: Fetcher<{
    agentConfiguration: AgentConfigurationType;
  }> = fetcher;

  const { data, error, mutate, isValidating } = useSWRWithDefaults(
    agentConfigurationId
      ? `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}`
      : null,
    agentConfigurationFetcher,
    { disabled }
  );

  return {
    agentConfiguration: data ? data.agentConfiguration : null,
    isAgentConfigurationLoading: !error && !data && !disabled,
    isAgentConfigurationError: error,
    isAgentConfigurationValidating: isValidating,
    mutateAgentConfiguration: mutate,
  };
}

interface AgentConfigurationFeedbacksByDescVersionProps {
  workspaceId: string;
  agentConfigurationId: string | null;
  limit: number;
  filter?: "unseen" | "all";
  version?: number;
  days?: number;
}

export function useAgentConfigurationFeedbacksByDescVersion({
  workspaceId,
  agentConfigurationId,
  limit,
  filter = "unseen",
  version,
  days,
}: AgentConfigurationFeedbacksByDescVersionProps) {
  const { fetcher } = useFetcher();
  const agentConfigurationFeedbacksFetcher: Fetcher<{
    feedbacks: (
      | AgentMessageFeedbackType
      | AgentMessageFeedbackWithMetadataType
    )[];
  }> = fetcher;

  const [hasMore, setHasMore] = useState(true);

  const { data, error, mutate, size, setSize, isLoading, isValidating } =
    useSWRInfiniteWithDefaults(
      (pageIndex: number, previousPageData) => {
        if (!agentConfigurationId) {
          return null;
        }

        // If we have reached the last page and there are no more
        // messages or the previous page has no messages, return null.
        if (previousPageData && previousPageData.feedbacks.length < limit) {
          setHasMore(false);
          return null;
        }

        // Build URLSearchParams fresh for each page to avoid param accumulation.
        const urlParams = new URLSearchParams({
          limit: limit.toString(),
          orderColumn: "id",
          orderDirection: "desc",
          withMetadata: "true",
          filter,
        });

        if (version !== undefined) {
          urlParams.set("version", version.toString());
        }

        if (days !== undefined) {
          urlParams.set("days", days.toString());
        }

        if (previousPageData !== null) {
          const lastIdValue =
            previousPageData.feedbacks[previousPageData.feedbacks.length - 1]
              .id;
          urlParams.append("lastValue", lastIdValue.toString());
        }
        return `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/feedbacks?${urlParams.toString()}`;
      },
      agentConfigurationFeedbacksFetcher,
      {
        revalidateAll: false,
        revalidateOnFocus: false,
      }
    );

  return {
    isLoadingInitialData: !error && !data,
    isAgentConfigurationFeedbacksError: error,
    isAgentConfigurationFeedbacksLoading: isLoading,
    isValidating,
    agentConfigurationFeedbacks: useMemo(
      () => (data ? data.flatMap((d) => (d ? d.feedbacks : [])) : []),
      [data]
    ),
    hasMore,
    mutateAgentConfigurationFeedbacks: mutate,
    setSize,
    size,
  };
}

export function useAgentConfigurationHistory({
  workspaceId,
  agentConfigurationId,
  limit,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
  limit?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const agentConfigurationHistoryFetcher: Fetcher<{
    history: AgentConfigurationType[];
  }> = fetcher;

  const queryParams = limit ? `?limit=${limit}` : "";
  const { data, error, mutate } = useSWRWithDefaults(
    agentConfigurationId
      ? `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/history${queryParams}`
      : null,
    agentConfigurationHistoryFetcher,
    { disabled }
  );

  return {
    agentConfigurationHistory: data?.history,
    isAgentConfigurationHistoryLoading: !error && !data,
    isAgentConfigurationHistoryError: error,
    mutateAgentConfigurationHistory: mutate,
  };
}

export function useAgentConfigurationLastAuthor({
  workspaceId,
  agentConfigurationId,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
}) {
  const { fetcher } = useFetcher();
  const userFetcher: Fetcher<{
    user: UserType;
  }> = fetcher;

  const { data, error } = useSWRWithDefaults(
    `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/last_author`,
    userFetcher
  );

  return {
    agentLastAuthor: data ? data.user : null,
    isLoading: !error && !data,
    isError: error,
  };
}

export function useAgentUsage({
  workspaceId,
  agentConfigurationId,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const agentUsageFetcher: Fetcher<GetAgentUsageResponseBody> = fetcher;
  const fetchUrl = agentConfigurationId
    ? `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/usage`
    : null;
  const { data, error, mutate } = useSWRWithDefaults(
    fetchUrl,
    agentUsageFetcher,
    { disabled }
  );

  return {
    agentUsage: data ? data.agentUsage : null,
    isAgentUsageLoading: !error && !data && !disabled,
    isAgentUsageError: error,
    mutateAgentUsage: mutate,
  };
}

export function useAgentAnalytics({
  workspaceId,
  agentConfigurationId,
  period,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string | null;
  period: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const agentAnalyticsFetcher: Fetcher<GetAgentOverviewResponseBody> = fetcher;
  const fetchUrl = agentConfigurationId
    ? (() => {
        const params = new URLSearchParams({ days: String(period) });
        if (version) {
          params.set("version", version);
        }
        return `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/overview?${params.toString()}`;
      })()
    : null;
  const { data, error } = useSWRWithDefaults(fetchUrl, agentAnalyticsFetcher, {
    disabled,
  });

  return {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    agentAnalytics: data ? data : null,
    isAgentAnalyticsLoading: !error && !data && !disabled,
    isAgentAnalyticsError: error,
  };
}

export function useAgentObservabilitySummary({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const summaryFetcher: Fetcher<GetAgentSummaryResponseBody> = fetcher;
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/summary?days=${days}`;

  const { data, error, isValidating, mutate } = useSWRWithDefaults(
    disabled ? null : key,
    summaryFetcher
  );

  return {
    summaryText: data?.summaryText ?? null,
    isSummaryLoading: !error && !data && !disabled,
    isSummaryError: error,
    isSummaryValidating: isValidating,
    refetchSummary: mutate,
  };
}

export function useSlackChannelsLinkedWithAgent({
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const slackChannelsLinkedWithAgentFetcher: Fetcher<GetSlackChannelsLinkedWithAgentResponseBody> =
    fetcher;

  const { data, error, mutate } = useSWRWithDefaults(
    `/api/w/${workspaceId}/assistant/builder/slack/channels_linked_with_agent`,
    slackChannelsLinkedWithAgentFetcher,
    {
      disabled: !!disabled,
    }
  );

  return {
    provider: data?.provider ?? "slack",
    slackChannels: data?.slackChannels ?? emptyArray(),
    slackDataSource: data?.slackDataSource,
    isSlackChannelsLoading: !error && !data,
    isSlackChannelsError: error,
    mutateSlackChannels: mutate,
  };
}

// Convenient hooks to do CRUD operations on agent configurations

export function useDeleteAgentConfiguration({
  owner,
  agentConfiguration,
}: {
  owner: LightWorkspaceType;
  agentConfiguration?: LightAgentConfigurationType;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateRegardlessOfQueryParams: mutateAgentConfigurations } =
    useAgentConfigurations({
      workspaceId: owner.sId,
      agentsGetView: "list", // Anything would work
      disabled: true, // We only use the hook to mutate the cache
    });

  const { mutateAgentConfiguration } = useAgentConfiguration({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration?.sId ?? null,
    disabled: true, // We only use the hook to mutate the cache
  });

  const doDelete = async () => {
    if (!agentConfiguration) {
      return;
    }
    const res = await clientFetch(
      `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration.sId}`,
      {
        method: "DELETE",
      }
    );

    if (res.ok) {
      void mutateAgentConfiguration();
      void mutateAgentConfigurations();

      sendNotification({
        type: "success",
        title: t("toasts.assistants.deleteSuccessTitle", {
          name: agentConfiguration.name,
        }),
        description: t("toasts.assistants.deleteSuccessDesc", {
          name: agentConfiguration.name,
        }),
      });
    } else {
      const errorData = await getErrorFromResponse(res);

      sendNotification({
        type: "error",
        title: t("toasts.assistants.deleteErrorTitle", {
          name: agentConfiguration.name,
        }),
        description: t("toasts.assistants.errorDesc", {
          message: errorData.message,
        }),
      });
    }
    return res.ok;
  };

  return doDelete;
}

export function useBatchDeleteAgentConfigurations({
  owner,
  agentConfigurationIds,
}: {
  owner: LightWorkspaceType;
  agentConfigurationIds: string[];
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateRegardlessOfQueryParams: mutateAgentConfigurations } =
    useAgentConfigurations({
      workspaceId: owner.sId,
      agentsGetView: "list", // Anything would work
      disabled: true, // We only use the hook to mutate the cache
    });

  const doDelete = async () => {
    if (agentConfigurationIds.length === 0) {
      return;
    }
    const res = await clientFetch(
      `/api/w/${owner.sId}/assistant/agent_configurations/delete`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agentConfigurationIds,
        }),
      }
    );

    if (res.ok) {
      void mutateAgentConfigurations();

      sendNotification({
        type: "success",
        title: t("toasts.assistants.batchDeleteSuccessTitle"),
        description: t("toasts.assistants.batchDeleteSuccessDesc", {
          count: agentConfigurationIds.length,
        }),
      });
    } else {
      const errorData = await getErrorFromResponse(res);

      sendNotification({
        type: "error",
        title: t("toasts.assistants.batchDeleteErrorTitle"),
        description: t("toasts.assistants.errorDesc", {
          message: errorData.message,
        }),
      });
    }
    return res.ok;
  };

  return doDelete;
}

export function useUpdateUserFavorite({
  owner,
  agentConfigurationId,
}: {
  owner: LightWorkspaceType;
  agentConfigurationId: string;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateAgentConfiguration: mutateCurrentAgentConfiguration } =
    useAgentConfiguration({
      workspaceId: owner.sId,
      agentConfigurationId,
      disabled: true,
    });
  const { mutate: mutateAgentConfigurations } = useUnifiedAgentConfigurations({
    workspaceId: owner.sId,
    disabled: true,
  });

  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);

  const doUpdate = useCallback(
    async (userFavorite: boolean) => {
      setIsUpdatingFavorite(true);
      try {
        const body: PostAgentUserFavoriteRequestBody = {
          agentId: agentConfigurationId,
          userFavorite: userFavorite,
        };

        const res = await clientFetch(
          `/api/w/${owner.sId}/members/me/agent_favorite`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
          }
        );

        if (res.ok) {
          sendNotification({
            title: userFavorite
              ? t("toasts.assistants.favoriteAddedTitle")
              : t("toasts.assistants.favoriteRemovedTitle"),
            type: "success",
          });
          await mutateCurrentAgentConfiguration();
          await mutateAgentConfigurations();
          return true;
        } else {
          const data = await res.json();
          sendNotification({
            title: userFavorite
              ? t("toasts.assistants.favoriteAddErrorTitle")
              : t("toasts.assistants.favoriteRemoveErrorTitle"),
            description: data.error.message,
            type: "error",
          });
          return false;
        }
      } catch (error) {
        sendNotification({
          title: t("toasts.assistants.favoriteUpdateErrorTitle"),
          description:
            normalizeError(error).message ||
            t("toasts.assistants.unknownError"),
          type: "error",
        });
        return false;
      } finally {
        setIsUpdatingFavorite(false);
      }
    },
    [
      agentConfigurationId,
      mutateAgentConfigurations,
      mutateCurrentAgentConfiguration,
      owner.sId,
      sendNotification,
      t,
    ]
  );
  return { updateUserFavorite: doUpdate, isUpdatingFavorite };
}

export function useRestoreAgentConfiguration({
  owner,
  agentConfiguration,
}: {
  owner: LightWorkspaceType;
  agentConfiguration?: LightAgentConfigurationType;
}) {
  const { t } = useTranslation();
  const sendNotification = useSendNotification();
  const { mutateRegardlessOfQueryParams: mutateAgentConfigurations } =
    useAgentConfigurations({
      workspaceId: owner.sId,
      agentsGetView: "list", // Anything would work
      disabled: true, // We only use the hook to mutate the cache
    });

  const { mutateAgentConfiguration } = useAgentConfiguration({
    workspaceId: owner.sId,
    agentConfigurationId: agentConfiguration?.sId ?? null,
    disabled: true, // We only use the hook to mutate the cache
  });

  const doRestore = async () => {
    if (!agentConfiguration) {
      return;
    }
    const res = await clientFetch(
      `/api/w/${owner.sId}/assistant/agent_configurations/${agentConfiguration.sId}/restore`,
      {
        method: "POST",
      }
    );

    if (res.ok) {
      void mutateAgentConfiguration();
      void mutateAgentConfigurations();

      sendNotification({
        type: "success",
        title: t("toasts.assistants.restoreSuccessTitle", {
          name: agentConfiguration.name,
        }),
        description: t("toasts.assistants.restoreSuccessDesc", {
          name: agentConfiguration.name,
        }),
      });
    } else {
      const errorData = await getErrorFromResponse(res);

      sendNotification({
        type: "error",
        title: t("toasts.assistants.restoreErrorTitle", {
          name: agentConfiguration.name,
        }),
        description: t("toasts.assistants.errorDesc", {
          message: errorData.message,
        }),
      });
    }
    return res.ok;
  };

  return doRestore;
}

export function useBatchUpdateAgentTags({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const batchUpdateAgentTags = useCallback(
    async (
      agentIds: string[],
      body: { addTagIds?: string[]; removeTagIds?: string[] }
    ) => {
      await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/batch_update_tags`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agentIds,
            ...body,
          }),
        }
      );
    },
    [owner]
  );

  return batchUpdateAgentTags;
}

export function useBatchUpdateAgentScope({
  owner,
}: {
  owner: LightWorkspaceType;
}) {
  const batchUpdateAgentScope = useCallback(
    async (agentIds: string[], body: { scope: "visible" | "hidden" }) => {
      await clientFetch(
        `/api/w/${owner.sId}/assistant/agent_configurations/batch_update_scope`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            agentIds,
            ...body,
          }),
        }
      );
    },
    [owner]
  );

  return batchUpdateAgentScope;
}

export function useAgentUsageMetrics({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  interval = "day",
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  interval?: "day" | "week";
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetUsageMetricsResponse> = fetcher;
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/usage-metrics?days=${days}&interval=${interval}&timezone=${encodeURIComponent(BROWSER_TIMEZONE)}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    usageMetrics: data?.points ?? emptyArray(),
    isUsageMetricsLoading: !error && !data && !disabled,
    isUsageMetricsError: error,
    isUsageMetricsValidating: isValidating,
  };
}

export function useAgentContextOrigin(params: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const {
    workspaceId,
    agentConfigurationId,
    days = DEFAULT_PERIOD_DAYS,
    version,
    disabled,
  } = params;
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetContextOriginResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/source?days=${days}${versionParam}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    contextOrigin: data ?? { total: 0, buckets: emptyArray() },
    isContextOriginLoading: !error && !data && !disabled,
    isContextOriginError: error,
    isContextOriginValidating: isValidating,
  };
}

export function useAgentLatency({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetLatencyResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/latency?days=${days}${versionParam}&timezone=${encodeURIComponent(BROWSER_TIMEZONE)}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    latency: data?.points ?? emptyArray(),
    isLatencyLoading: !error && !data && !disabled,
    isLatencyError: error,
    isLatencyValidating: isValidating,
  };
}

export function useAgentErrorRate({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetErrorRateResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/error_rate?days=${days}${versionParam}&timezone=${encodeURIComponent(BROWSER_TIMEZONE)}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    errorRate: data?.points ?? emptyArray(),
    isErrorRateLoading: !error && !data && !disabled,
    isErrorRateError: error,
    isErrorRateValidating: isValidating,
  };
}

export function useAgentFeedbackDistribution({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetFeedbackDistributionResponse> = fetcher;
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/feedback-distribution?days=${days}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    feedbackDistribution: data?.points ?? emptyArray(),
    isFeedbackDistributionLoading: !error && !data && !disabled,
    isFeedbackDistributionError: error,
    isFeedbackDistributionValidating: isValidating,
  };
}

export function useAgentVersionMarkers({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetVersionMarkersResponse> = fetcher;
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/version-markers?days=${days}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    versionMarkers: data?.versionMarkers ?? emptyArray(),
    isVersionMarkersLoading: !error && !data && !disabled,
    isVersionMarkersError: error,
    isVersionMarkersValidating: isValidating,
  };
}

export function useAgentToolExecution({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetToolExecutionResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/tool-execution?days=${days}${versionParam}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    toolExecutionByVersion: data?.byVersion ?? emptyArray(),
    isToolExecutionLoading: !error && !data && !disabled,
    isToolExecutionError: error,
    isToolExecutionValidating: isValidating,
  };
}

export function useAgentSkillExecution({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetSkillExecutionResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/skill-execution?days=${days}${versionParam}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    skillExecutionByVersion: data?.byVersion ?? emptyArray(),
    skillExecutionBySource: data?.bySource ?? emptyArray(),
    isSkillExecutionLoading: !error && !data && !disabled,
    isSkillExecutionError: error,
    isSkillExecutionValidating: isValidating,
  };
}

type AgentToolLatencyResult = {
  toolLatencyRows: ToolLatencyRow[];
  isToolLatencyLoading: boolean;
  isToolLatencyError: unknown;
  isToolLatencyValidating: boolean;
};

export function useAgentToolLatency({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  view,
  serverName,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  view: ToolLatencyView;
  serverName?: string;
  disabled?: boolean;
}): AgentToolLatencyResult {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetToolLatencyResponse> = fetcher;
  const params = new URLSearchParams({ days: days.toString(), view });
  if (version) {
    params.set("version", version);
  }
  if (serverName) {
    params.set("serverName", serverName);
  }
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/tool-latency?${params.toString()}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    toolLatencyRows: data?.rows ?? emptyArray(),
    isToolLatencyLoading: !error && !data && !disabled,
    isToolLatencyError: error,
    isToolLatencyValidating: isValidating,
  };
}

export function useAgentToolStepIndex({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetToolStepIndexResponse> = fetcher;
  const versionParam = version ? `&version=${encodeURIComponent(version)}` : "";
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/tool-step-index?days=${days}${versionParam}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    toolStepIndexByStep: data?.byStep ?? emptyArray(),
    isToolStepIndexLoading: !error && !data && !disabled,
    isToolStepIndexError: error,
    isToolStepIndexValidating: isValidating,
  };
}

export function useAgentDatasourceRetrieval({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetDatasourceRetrievalResponse> = fetcher;
  const params = new URLSearchParams({ days: days.toString() });
  if (version) {
    params.set("version", version);
  }
  const key = `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/datasource-retrieval?${params.toString()}`;

  const { data, error, isValidating } = useSWRWithDefaults(
    disabled ? null : key,
    fetcherFn
  );

  return {
    datasourceRetrieval: data?.datasources ?? emptyArray(),
    totalRetrievals: data?.total ?? 0,
    isDatasourceRetrievalLoading: !error && !data && !disabled,
    isDatasourceRetrievalError: error,
    isDatasourceRetrievalValidating: isValidating,
  };
}

export function useAgentDatasourceRetrievalDocuments({
  workspaceId,
  agentConfigurationId,
  days = DEFAULT_PERIOD_DAYS,
  version,
  mcpServerConfigIds,
  mcpServerName,
  dataSourceId,
  limit = 50,
  disabled,
}: {
  workspaceId: string;
  agentConfigurationId: string;
  days?: number;
  version?: string;
  mcpServerConfigIds: string[] | null;
  // For servers without config IDs (like data_sources_file_system), use name.
  mcpServerName: string | null;
  dataSourceId: string | null;
  limit?: number;
  disabled?: boolean;
}) {
  const { fetcher } = useFetcher();
  const fetcherFn: Fetcher<GetDatasourceRetrievalDocumentsResponse> = fetcher;
  const hasConfigIds = mcpServerConfigIds && mcpServerConfigIds.length > 0;
  // Allow query if we have either config IDs or server name.
  const isDisabled =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    disabled || (!hasConfigIds && !mcpServerName) || !dataSourceId;

  const params = new URLSearchParams({ days: days.toString() });
  if (version) {
    params.set("version", version);
  }
  if (hasConfigIds) {
    params.set(
      "mcpServerConfigIds",
      [...new Set(mcpServerConfigIds)].join(",")
    );
  }
  if (mcpServerName) {
    params.set("mcpServerName", mcpServerName);
  }
  if (dataSourceId) {
    params.set("dataSourceId", dataSourceId);
  }
  params.set("limit", limit.toString());

  const key = isDisabled
    ? null
    : `/api/w/${workspaceId}/assistant/agent_configurations/${agentConfigurationId}/observability/datasource-retrieval-documents?${params.toString()}`;

  const { data, error, isValidating } = useSWRWithDefaults(key, fetcherFn);

  return {
    documents: data?.documents ?? emptyArray(),
    groups: data?.groups ?? emptyArray(),
    total: data?.total ?? 0,
    isDatasourceRetrievalDocumentsLoading: !error && !data && !isDisabled,
    isDatasourceRetrievalDocumentsError: error,
    isDatasourceRetrievalDocumentsValidating: isValidating,
  };
}

export type MemberDisplayInfo = {
  fullName: string;
  email: string | null;
  image: string | null;
};

function buildMemberDetailsSwrKey(
  workspaceId: string,
  userIds: string[]
): string | null {
  const normalizedUserIds = [...new Set(userIds.filter(Boolean))].sort();
  if (normalizedUserIds.length === 0) {
    return null;
  }
  if (normalizedUserIds.length === 1) {
    return `/api/w/${workspaceId}/members/${normalizedUserIds[0]}`;
  }
  const sIdsKey = normalizedUserIds.join(",");
  return `/api/w/${workspaceId}/members/batch?sIds=${encodeURIComponent(sIdsKey)}`;
}

export function useMemberDetails({
  workspaceId,
  userIds,
}: {
  workspaceId: string;
  userIds: string[];
}) {
  const { fetcher } = useFetcher();
  const normalizedUserIds = useMemo(
    () => [...new Set(userIds.filter(Boolean))].sort(),
    [userIds]
  );
  const swrKey = useMemo(
    () => buildMemberDetailsSwrKey(workspaceId, normalizedUserIds),
    [workspaceId, normalizedUserIds]
  );

  const memberDetailsFetcher = useCallback(
    async (
      key: string
    ): Promise<
      | { kind: "single"; member: GetMemberResponseBody["member"] }
      | { kind: "batch"; membersBySId: Record<string, MemberDisplayInfo> }
    > => {
      if (key.includes("/members/batch?")) {
        const url = new URL(key, "https://dust.local");
        const sIdsParam = url.searchParams.get("sIds");
        if (!sIdsParam) {
          return { kind: "batch", membersBySId: {} };
        }

        const membersBySId: Record<string, MemberDisplayInfo> = {};
        for (const memberSId of sIdsParam.split(",")) {
          try {
            const response = (await fetcher(
              `/api/w/${workspaceId}/members/${memberSId}`
            )) as GetMemberResponseBody;

            membersBySId[memberSId] = {
              fullName: response.member.fullName,
              email: response.member.email,
              image: response.member.image,
            };
          } catch {
            // Member lookup can fail for privacy or membership reasons.
          }
        }

        return { kind: "batch", membersBySId };
      }

      const response = (await fetcher(key)) as GetMemberResponseBody;
      return { kind: "single", member: response.member };
    },
    [fetcher, workspaceId]
  );

  const { data, error, mutate, isValidating, isLoading } = useSWRWithDefaults(
    swrKey,
    memberDetailsFetcher
  );

  const userDetails = data?.kind === "single" ? data.member : undefined;
  const membersBySId =
    data?.kind === "batch"
      ? data.membersBySId
      : userDetails
        ? {
            [userDetails.id]: {
              fullName: userDetails.fullName,
              email: userDetails.email,
              image: userDetails.image,
            },
          }
        : {};

  return {
    userDetails,
    membersBySId,
    isMembersLoading: !error && isLoading && !!swrKey,
    isMembersError: error,
    isMembersValidating: isValidating,
    mutateMembers: mutate,
  };
}
